<template>
    <div>
        <v-expansion-panels class="mb-4 elevation-1">
            <v-expansion-panel>
                <v-expansion-panel-header
                    color="#eeeeee"
                    class="text-h5 my-0 font-weight-bold"
                >
                    Invoice Reference #{{ iterator
                    }}<span v-if="hasModalityOverride"
                        >&nbsp;— {{ resolvedModality }}</span
                    >
                    <template v-slot:actions>
                        <v-btn
                            v-if="!shipmentIsNew && !view"
                            icon
                            @click.stop="editable = !editable"
                        >
                            <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn v-if="!view" icon @click.stop="deleteShipment()">
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                        <v-icon>$expand</v-icon>
                    </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <!-- first-form -->
                    <v-form v-model="valid" class="py-2" :disabled="!editable">
                        <v-row no-gutters class="mt-4">
                            <v-col
                                cols="12"
                                xl="4"
                                lg="4"
                                md="4"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.invoiceNumber"
                                    prepend-icon="mdi-invoice-text-outline"
                                    label="Invoice Number"
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                    hide-details
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="4"
                                lg="4"
                                md="4"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.trm"
                                    prepend-icon="mdi-currency-usd"
                                    label="TRM"
                                    type="number"
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    @input="onTrmInput"
                                    hide-details
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="4"
                                lg="4"
                                md="4"
                                sm="6"
                                class="pl-2"
                            >
                                <v-select
                                    v-model="referenceModality"
                                    :items="shipmentData.modality"
                                    label="Modality"
                                    hint="Only change if this reference uses a different modality"
                                    persistent-hint
                                    prepend-icon="mdi-receipt-text-outline"
                                    hide-selected
                                    :menu-props="{
                                        bottom: true,
                                        offsetY: true,
                                    }"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                    <!-- data-table -->
                    <v-data-table
                        class="mt-4 elevation-1"
                        :headers="filteredHeaders"
                        :items="itemsFromShipment"
                        item-value="id"
                        :loading="quoteLoading"
                        :mobile-breakpoint="0"
                        disable-pagination
                        hide-default-footer
                        fixed-header
                    >
                        <!--ITEMS-->
                        <template v-slot:[`item.description`]="{ item }">
                            <div v-if="!item.code" class="d-flex justify-right">
                                <v-select
                                    :disabled="!editable"
                                    class="text-body-2 font-weight-regular pb-4"
                                    dense
                                    single-line
                                    v-model="item.description"
                                    :items="itemsAvailable"
                                    item-text="description"
                                    hide-details
                                    hide-selected
                                    :rules="[rules.required]"
                                    @change="
                                        () => {
                                            pushItem(item)
                                            getTotalValues()
                                            structureData(item.code)
                                        }
                                    "
                                    :menu-props="{
                                        bottom: true,
                                        offsetY: true,
                                    }"
                                ></v-select>
                            </div>
                            <div v-else class="d-flex justify-center">
                                <p class="text-capitalize">
                                    {{ item.description }}
                                </p>
                            </div>
                        </template>
                        <template v-slot:[`item.ci`]="{ item }">
                            <div>
                                <v-text-field
                                    :disabled="!editable"
                                    class="font-weight-regular text-body-2"
                                    dense
                                    single-line
                                    v-model="item.ci"
                                    regular
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                ></v-text-field>
                            </div>
                        </template>
                        <template v-slot:[`item.oc`]="{ item }">
                            <div class="d-flex justify-center">
                                <p class="text-capitalize">
                                    {{ item.oc }}
                                </p>
                            </div>
                        </template>
                        <template v-slot:[`item.weight`]="{ item }">
                            <div>
                                <v-text-field
                                    :disabled="!editable"
                                    class="font-weight-regular text-body-2"
                                    type="number"
                                    dense
                                    single-line
                                    v-model="item.weight"
                                    regular
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    @input="
                                        event => {
                                            item.weight = parseFloat(event) || 0
                                            getTotalValues()
                                            structureData()
                                        }
                                    "
                                    suffix="kg"
                                ></v-text-field>
                            </div>
                        </template>
                        <template v-slot:[`item.quantity`]="{ item }">
                            <div>
                                <v-text-field
                                    :disabled="!editable"
                                    class="font-weight-regular text-body-2"
                                    type="number"
                                    dense
                                    single-line
                                    v-model="item.quantity"
                                    regular
                                    :rules="[
                                        rules.validateNumber,
                                        rules.required,
                                        rules.quantityRange(item),
                                    ]"
                                    @input="
                                        event => {
                                            item.quantity =
                                                parseFloat(event) || 0
                                            getTotalValues()
                                            structureData(item.code)
                                        }
                                    "
                                ></v-text-field>
                            </div>
                        </template>
                        <template v-slot:[`item.unitWeight`]="{ item }">
                            <div class="d-flex justify-center">
                                <p class="text-capitalize">
                                    {{
                                        item.weight && item.quantity
                                            ? (
                                                  item.weight / item.quantity
                                              ).toFixed(2)
                                            : '-'
                                    }}
                                </p>
                            </div>
                        </template>
                        <template v-slot:[`item.unitPrice`]="{ item }">
                            <div>
                                <v-text-field
                                    :disabled="!editable"
                                    class="font-weight-regular text-body-2"
                                    type="number"
                                    step="any"
                                    dense
                                    single-line
                                    v-model="item.unitPrice"
                                    regular
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    @input="onUnitPriceInput(item)"
                                    suffix="$"
                                ></v-text-field>
                            </div>
                        </template>
                        <template v-slot:[`item.totalPrice`]="{ item }">
                            <div class="d-flex justify-end">
                                <p class="text-capitalize">
                                    {{
                                        formatCurrency(
                                            'USD',
                                            item.totalPrice || 0,
                                            3
                                        )
                                    }}
                                </p>
                            </div>
                        </template>
                        <template v-slot:[`item.valueInCOP`]="{ item }">
                            <div class="d-flex justify-end">
                                <p class="text-capitalize">
                                    {{
                                        formatCurrency(
                                            'COP',
                                            item.totalPrice *
                                                shipmentFormData.trm || 0
                                        )
                                    }}
                                </p>
                            </div>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                            <div class="d-flex justify-center my-0">
                                <v-btn
                                    v-if="editable"
                                    small
                                    icon
                                    class="mr-2"
                                    @click="deleteItem(item)"
                                >
                                    <v-icon>
                                        mdi-delete
                                    </v-icon>
                                </v-btn>
                            </div>
                        </template>
                        <!--Body append-->
                        <template v-slot:[`body.append`]>
                            <!--"+ Add New Item"-->

                            <td :colspan="headers.length" class="text-center">
                                <v-btn
                                    @click="addNewItem()"
                                    block
                                    plain
                                    :disabled="
                                        !editable ||
                                            validateNewShipment === 0 ||
                                            validateItems.length === 0
                                    "
                                >
                                    <v-icon small>
                                        mdi-plus
                                    </v-icon>
                                    Add New Item
                                </v-btn>
                            </td>

                            <!--FOOTER-->
                            <tr :style="{ 'background-color': '#eeeeee' }">
                                <td
                                    v-if="isPlanVallejo"
                                    class="font-weight-bold"
                                ></td>
                                <td class="font-weight-bold"></td>
                                <td class="font-weight-bold justify-end">
                                    TOTALS
                                </td>
                                <td class="font-weight-bold">
                                    <p
                                        class="d-flex justify-center align-right my-0"
                                    >
                                        {{ totalWeight.toFixed(2) }} kg
                                    </p>
                                </td>
                                <td class="font-weight-bold">
                                    <p
                                        class="d-flex justify-center align-right my-0"
                                    >
                                        {{ totalQuantity.toFixed(2) }}
                                    </p>
                                </td>
                                <td class="font-weight-bold"></td>
                                <td class="font-weight-bold"></td>
                                <td class="font-weight-bold">
                                    <p
                                        class="d-flex justify-end align-right my-0"
                                    >
                                        {{ formatCurrency('USD', totalUSD, 3) }}
                                    </p>
                                </td>
                                <td class="font-weight-bold">
                                    <p
                                        class="d-flex justify-end align-right my-0"
                                    >
                                        {{ formatCurrency('COP', totalCOP) }}
                                    </p>
                                </td>
                                <td class="font-weight-bold"></td>
                            </tr>
                        </template>
                    </v-data-table>
                    <!-- second-form -->
                    <v-form v-model="valid2" class="py-2" :disabled="!editable">
                        <v-row no-gutters class="mt-4">
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-select
                                    v-model="shipmentFormData.shipmentType"
                                    :items="shipmentData.shipmentType"
                                    label="Shipment Type"
                                    hide-details
                                    prepend-icon="mdi-plane-train"
                                    hide-selected
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                    :menu-props="{
                                        bottom: true,
                                        offsetY: true,
                                    }"
                                />
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="my-1 mt-0">
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-menu
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                    v-model="menu"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="shipmentFormData.depDate"
                                            label="Departure Date"
                                            prepend-icon="mdi-airplane-clock"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            hide-details
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="shipmentFormData.depDate"
                                        @input="
                                            () => {
                                                structureData(), (menu = false)
                                            }
                                        "
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-select
                                    v-model="shipmentFormData.arrivalPort"
                                    :items="shipmentData.arrivalPort"
                                    label="Port of Arrival"
                                    hide-details
                                    prepend-icon="mdi-plane-train"
                                    hide-selected
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                    :menu-props="{
                                        bottom: true,
                                        offsetY: true,
                                    }"
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.ShippingLine"
                                    prepend-icon="mdi-shipping-pallet"
                                    label="Shipping Line"
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                    hide-details
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.motonave"
                                    prepend-icon="mdi-truck-cargo-container"
                                    label="Motonave"
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="my-1 mt-0">
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.transitTime"
                                    prepend-icon="mdi-calendar-clock"
                                    label="Transit Time in days"
                                    type="number"
                                    @input="
                                        event => {
                                            shipmentFormData.transitTime =
                                                parseFloat(event) || 0
                                            structureData()
                                        }
                                    "
                                    :rules="[rules.required, rules.number1]"
                                    hide-details
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-menu
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                    v-model="menu3"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="
                                                shipmentFormData.estimatedDateArrival
                                            "
                                            label="Estimated Date of Arrival"
                                            prepend-icon="mdi-calendar-check"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            hide-details
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="
                                            shipmentFormData.estimatedDateArrival
                                        "
                                        @input="
                                            () => {
                                                structureData(), (menu3 = false)
                                            }
                                        "
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.numberDim"
                                    prepend-icon="mdi-invoice-import-outline"
                                    label="DIM Number"
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="my-1 mt-0">
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-menu
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                    v-model="menu1"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="
                                                shipmentFormData.acceptanceDateDIM
                                            "
                                            label="Acceptance Date DIM"
                                            prepend-icon="mdi-calendar-check"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            hide-details
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="
                                            shipmentFormData.acceptanceDateDIM
                                        "
                                        @input="
                                            () => {
                                                structureData(), (menu1 = false)
                                            }
                                        "
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-menu
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                    v-model="menu4"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="shipmentFormData.DateOfDIM"
                                            label="Dim Lift Date"
                                            prepend-icon="mdi-calendar-check"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            hide-details
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="shipmentFormData.DateOfDIM"
                                        @input="
                                            () => {
                                                structureData(), (menu4 = false)
                                            }
                                        "
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.ivaDim"
                                    prepend-icon="mdi-cash"
                                    label="IVA DIM"
                                    type="number"
                                    @input="
                                        event => {
                                            shipmentFormData.ivaDim =
                                                parseFloat(event) || 0
                                            structureData()
                                        }
                                    "
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="my-1 mt-0">
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.containerNumber"
                                    prepend-icon="mdi-train-car-container"
                                    label="Container Number"
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                    hide-details
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-select
                                    v-model="shipmentFormData.containerSize"
                                    :items="shipmentData.containerSize"
                                    label="Container Size"
                                    hide-details
                                    prepend-icon="mdi-train-car-container"
                                    hide-selected
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                    :menu-props="{
                                        bottom: true,
                                        offsetY: true,
                                    }"
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.transportDoc"
                                    prepend-icon="mdi-text-box-outline"
                                    label="Transport Document"
                                    @input="structureData()"
                                    :rules="[rules.required]"
                                    hide-details
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-menu
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                    v-model="menu6"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="
                                                shipmentFormData.dateDocTransport
                                            "
                                            label="Date of Transport Document"
                                            prepend-icon="mdi-calendar-check"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            hide-details
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="
                                            shipmentFormData.dateDocTransport
                                        "
                                        @input="
                                            () => {
                                                structureData(), (menu6 = false)
                                            }
                                        "
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="my-1 mt-0">
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.fobDimValue"
                                    prepend-icon="mdi-currency-usd"
                                    label="FOB DIM Value"
                                    type="number"
                                    @input="
                                        event => {
                                            shipmentFormData.fobDimValue =
                                                parseFloat(event) || 0
                                            structureData()
                                        }
                                    "
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    hide-details
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.unitFOB"
                                    prepend-icon="mdi-currency-usd"
                                    label="FOB Unit Value"
                                    type="number"
                                    @input="
                                        event => {
                                            shipmentFormData.unitFOB =
                                                parseFloat(event) || 0

                                            structureData()
                                        }
                                    "
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="my-1 mt-0">
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.freight"
                                    prepend-icon="mdi-currency-usd"
                                    type="number"
                                    @input="
                                        event => {
                                            shipmentFormData.freight =
                                                parseFloat(event) || 0
                                            structureData()
                                        }
                                    "
                                    label="Freight Value"
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    hide-details
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.insurance"
                                    prepend-icon="mdi-currency-usd"
                                    label="Insurance"
                                    type="number"
                                    @input="
                                        event => {
                                            shipmentFormData.insurance =
                                                parseFloat(event) || 0
                                            structureData()
                                        }
                                    "
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    hide-details
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    v-model="shipmentFormData.otherCosts"
                                    prepend-icon="mdi-currency-usd"
                                    label="Other Costs"
                                    type="number"
                                    @input="
                                        event => {
                                            shipmentFormData.otherCosts =
                                                parseFloat(event) || 0
                                            structureData()
                                        }
                                    "
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="my-4 mt-4">
                            <v-spacer></v-spacer>
                        </v-row>
                        <v-row no-gutters class="my-1 mt-0">
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-menu
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                    v-model="menu2"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="
                                                shipmentFormData.realDateArrival
                                            "
                                            label="Actual Date of Arrival"
                                            prepend-icon="mdi-calendar-check"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            hide-details
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="
                                            shipmentFormData.realDateArrival
                                        "
                                        @input="
                                            () => {
                                                structureData(), (menu2 = false)
                                            }
                                        "
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-menu
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                    v-model="menu5"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="
                                                shipmentFormData.enteredDate
                                            "
                                            label="Date Entered Warehouse"
                                            prepend-icon="mdi-calendar-check"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            hide-details
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="shipmentFormData.enteredDate"
                                        @input="
                                            () => {
                                                structureData(), (menu5 = false)
                                            }
                                        "
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col></v-col>
                            <v-col
                                cols="12"
                                xl="3"
                                lg="3"
                                md="3"
                                sm="6"
                                class="pl-2"
                            >
                                <v-text-field
                                    readonly
                                    disabled
                                    v-model="computedTotalCustoms"
                                    prepend-icon="mdi-currency-usd"
                                    label="Total Customs Value"
                                    :rules="[
                                        rules.required,
                                        rules.validateNumber,
                                    ]"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                    <v-divider class="my-4" />
                    <v-row no-gutters class="my-3">
                        <v-col cols="12" class="pl-2">
                            <h4 class="text-subtitle-1 font-weight-black">
                                ATTACHMENTS:
                            </h4>
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="my-2">
                        <v-col
                            cols="12"
                            xl="2"
                            lg="2"
                            md="3"
                            sm="12"
                            class="pl-2 d-flex align-center"
                        >
                            <span class="font-weight-bold text-uppercase"
                                >Files:</span
                            >
                        </v-col>
                        <v-col
                            cols="12"
                            xl="10"
                            lg="10"
                            md="9"
                            sm="12"
                            class="pl-2"
                        >
                            <div class="d-flex flex-wrap align-center">
                                <v-btn
                                    v-for="(file, fileIndex) in attachmentFiles"
                                    :key="
                                        `files-${fileIndex}-${attachmentFileKey(
                                            file
                                        )}`
                                    "
                                    color="transparent"
                                    elevation="0"
                                    small
                                    class="ma-1 text-capitalize text--darken-2 pa-2"
                                >
                                    <v-icon
                                        class="mr-1"
                                        color="primary"
                                        :style="{ 'font-size': '17px' }"
                                    >
                                        mdi-paperclip
                                    </v-icon>
                                    <span
                                        @click.stop="openAttachment(file)"
                                        :style="{
                                            'font-size': '11px',
                                            color: '#2b81d6',
                                            'text-decoration': 'underline',
                                            cursor: 'pointer',
                                        }"
                                        >{{ attachmentFileKey(file) }}</span
                                    >
                                    <v-icon
                                        v-if="!view"
                                        :disabled="!editable"
                                        @click.stop="
                                            removeAttachment(fileIndex)
                                        "
                                        class="ml-1"
                                        small
                                    >
                                        mdi-close-circle
                                    </v-icon>
                                </v-btn>
                                <v-btn
                                    v-if="!view"
                                    :disabled="!editable"
                                    @click.stop="triggerFileInput"
                                    color="accent"
                                    icon
                                    class="ml-2"
                                    plain
                                >
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                                <input
                                    ref="fileInput_files"
                                    class="d-none"
                                    type="file"
                                    multiple
                                    :disabled="!editable || view"
                                    @change="handleFileSelect"
                                />
                            </div>
                        </v-col>
                    </v-row>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'
import { storage } from '@/services/firebase'
import {
    findAbleQuote,
    isCopQuote,
    resolveUsdUnitPrice,
} from './comexUsd'

const LEGACY_ATTACHMENT_KEYS = [
    'attachedInvoice',
    'attachedDIM',
    'attachedDocTransport',
    'attachedOtherFiles',
]

const ATTACHMENT_FILE_KEY = 'files'

export default {
    name: 'ComexShipmentForm',
    props: {
        iterator: {
            type: Number,
            default: () => 1,
        },
        maxValues: {
            type: Array,
            default: () => [],
            required: true,
        },
        request: {
            type: Object,
            default: () => {},
            required: true,
        },
        items: {
            type: Array,
            default: () => [],
            required: true,
        },
        itemsInRequest: {
            type: Array,
            default: () => [],
            required: true,
        },
        newShipment: {
            type: Boolean,
            default: () => false,
        },
        isEditable: {
            type: Boolean,
            default: () => false,
        },
        shipmentId: {
            type: String,
            required: true,
        },
        validateNewShipment: {
            type: Number,
            required: true,
        },
        formShipment: {
            type: Object,
            default: () => {},
        },
        view: {
            type: Boolean,
            default: () => false,
        },
    },
    data() {
        return {
            editable: false,
            shipmentIsNew: false,
            menu: false,
            menu1: false,
            menu2: false,
            menu3: false,
            menu4: false,
            menu5: false,
            menu6: false,
            valid: false,
            valid2: false,
            shipmentFormData: {},
            shipmentData: {
                shipmentType: ['Maritime', 'Aerial'],
                arrivalPort: [
                    'Barranquilla-SPRB',
                    'Cartagena',
                    'Buenaventura',
                    'Bogotá',
                    'Rionegro',
                ],
                containerSize: ['20"', '40"', '45"'],
                modality: ['Plan Vallejo', 'Courier', 'Ordinary'],
            },
            rules: {
                required: v => !!v || 'Required',
                number1: v => v > 0 || 'Error',
                number: v => v >= 0 || 'Error',
                validateNumber: v => {
                    const numberValue = parseFloat(v)
                    return (
                        (!isNaN(numberValue) && numberValue > 0) ||
                        'Invalid Number'
                    )
                },
                quantityRange: item => value => {
                    const maxValueObj = this.maxValues.find(
                        mv => mv.code === item.code
                    )
                    if (
                        maxValueObj &&
                        maxValueObj.received - maxValueObj.processed >= 0
                    ) {
                        return true
                    } else {
                        return `${value} exceeds the amount received`
                    }
                },
            },
            headers: [
                {
                    text: 'PRODUCT',
                    value: 'description',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'CI',
                    value: 'ci',
                    align: 'center',
                    sortable: false,
                    width: '100',
                },
                {
                    text: 'OC',
                    value: 'oc',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'WEIGHT (Kg)',
                    value: 'weight',
                    align: 'center',
                    sortable: false,
                    width: '120',
                },
                {
                    text: 'QUANTITY',
                    value: 'quantity',
                    align: 'center',
                    sortable: false,
                    width: '95',
                },
                {
                    text: 'UNIT WEIGHT (Kg)',
                    value: 'unitWeight',
                    align: 'center',
                    sortable: false,
                    width: '120',
                },
                {
                    text: 'USD VALUE PER UNIT',
                    value: 'unitPrice',
                    align: 'center',
                    sortable: false,
                    width: '120',
                },
                {
                    text: 'USD VALUE',
                    value: 'totalPrice',
                    align: 'right',
                    sortable: false,
                    width: '120',
                },
                {
                    text: 'COP VALUE',
                    value: 'valueInCOP',
                    align: 'right',
                    sortable: false,
                },
                {
                    text: 'ACTIONS',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                },
            ],
            quoteLoading: false,
            totalWeight: 0,
            totalQuantity: 0,
            totalUSD: 0,
            totalCOP: 0,
            manualUnitPrices: {},
            autoUnitPrices: {},
            localAttachments: { files: {} },
            attachmentFiles: [],
            attachmentFileNames: [],
            companyId: JSON.parse(localStorage.getItem('company')),
            itemsFromShipment: [],
            itemsAvailable: [],
        }
    },
    computed: {
        computedTotalCustoms() {
            const {
                fobDimValue,
                freight,
                insurance,
                otherCosts,
            } = this.shipmentFormData
            const totalCustoms =
                Number(fobDimValue || 0) +
                Number(freight || 0) +
                Number(insurance || 0) +
                Number(otherCosts || 0)
            return totalCustoms
        },
        resolvedModality() {
            return this.shipmentFormData.modality || this.request.modality || ''
        },
        hasModalityOverride() {
            return Boolean(
                this.shipmentFormData.modality &&
                    this.shipmentFormData.modality !== this.request.modality
            )
        },
        referenceModality: {
            get() {
                return this.resolvedModality || null
            },
            set(value) {
                if (!value || value === this.request.modality) {
                    this.$delete(this.shipmentFormData, 'modality')
                } else {
                    this.$set(this.shipmentFormData, 'modality', value)
                }
                this.structureData()
            },
        },
        isPlanVallejo() {
            return this.resolvedModality === 'Plan Vallejo'
        },
        filteredHeaders() {
            return this.headers.filter(
                header => header.value !== 'ci' || this.isPlanVallejo
            )
        },
        getTotalPrice() {
            return () => {
                return id => {
                    const item = this.itemsFromShipment.find(
                        obj => obj.code === id
                    )
                    return item ? item.quantity * item.unitPrice : 0
                }
            }
        },
        validateItems() {
            let itemsAvailable = []
            this.itemsInRequest.forEach(item => {
                const indexAvalaible = this.maxValues.findIndex(
                    obj => obj.code === item.code
                )
                if (indexAvalaible > -1) {
                    const maxValue = this.maxValues[indexAvalaible].maxValue
                    if (maxValue > 0) {
                        itemsAvailable.push({
                            description: item.description,
                            code: item.code,
                        })
                    }
                }
            })
            this.itemsFromShipment.forEach(obj => {
                const itemIndex = itemsAvailable.findIndex(
                    item => item.code === obj.code
                )
                if (itemIndex > -1) {
                    itemsAvailable.splice(itemIndex, 1)
                }
            })
            return itemsAvailable
        },
    },
    async mounted() {
        try {
            this.loading = true
            this.editable = this.isEditable
            this.shipmentIsNew = this.newShipment
            if (!this.shipmentIsNew) {
                this.shipmentFormData = _.cloneDeep(this.formShipment) || {}
                this.itemsFromShipment = _.cloneDeep(this.items)
                this.normalizeUsdUnitPrices()
            }

            this.initializeAttachments()
            if (!this.shipmentIsNew) {
                await this.loadAttachments()
            }

            this.getTotalValues()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        formatCurrency(currency, value, maximumFractionDigits = 2) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency,
                maximumFractionDigits,
            }).format(value)
        },
        onTrmInput(event) {
            this.shipmentFormData.trm = parseFloat(event) || 0
            this.normalizeUsdUnitPrices()
            this.getTotalValues()
            this.structureData()
        },
        onUnitPriceInput(item) {
            if (item.code) {
                this.$set(this.manualUnitPrices, item.code, true)
            }
            const unitPrice = Number(item.unitPrice)
            if (Number.isFinite(unitPrice)) {
                item.totalPrice = unitPrice * (Number(item.quantity) || 0)
            }
            this.getTotalValues()
            this.structureData(item.code)
        },
        normalizeUsdUnitPrices() {
            const trm = Number(this.shipmentFormData.trm)
            this.itemsFromShipment = this.itemsFromShipment.map(item => {
                if (item.code && this.manualUnitPrices[item.code]) return item
                const quote = findAbleQuote(this.request, item)
                const unitPrice = resolveUsdUnitPrice(
                    item,
                    quote,
                    trm,
                    item.code ? this.autoUnitPrices[item.code] : undefined
                )
                if (item.code && isCopQuote(quote) && trm > 0) {
                    const native = Number(
                        quote.priceBeforeTRM ?? quote.price
                    )
                    const expected =
                        Number.isFinite(native) && native > 0
                            ? Math.round((native / trm) * 1000) / 1000
                            : null
                    if (
                        expected != null &&
                        Math.abs(unitPrice - expected) < 0.001
                    ) {
                        this.$set(this.autoUnitPrices, item.code, unitPrice)
                    }
                }
                const quantity = Number(item.quantity) || 0
                const totalPrice =
                    unitPrice === Number(item.unitPrice)
                        ? Number(item.totalPrice) || unitPrice * quantity
                        : unitPrice * quantity
                return { ...item, unitPrice, totalPrice }
            })
        },
        getTotalValues() {
            this.totalWeight = 0
            this.totalQuantity = 0
            this.totalUSD = 0
            this.totalCOP = 0
            this.itemsFromShipment.forEach(item => {
                this.totalWeight += Number(item.weight) || 0
                this.totalQuantity += Number(item.quantity) || 0
                this.totalUSD += Number(item.totalPrice) || 0
            })
            this.totalCOP = this.totalUSD * this.shipmentFormData.trm || 0
        },
        collectLegacyAttachmentNames() {
            const names = new Set()
            LEGACY_ATTACHMENT_KEYS.forEach(key => {
                const list = this.shipmentFormData[key]
                if (Array.isArray(list)) {
                    list.forEach(name => names.add(name))
                }
            })
            return names
        },
        initializeAttachments() {
            const names = new Set(this.collectLegacyAttachmentNames())

            if (this.shipmentFormData.attachments?.files) {
                this.shipmentFormData.attachments.files.forEach(file => {
                    const name = this.resolveAttachmentName(file)
                    if (name) names.add(name)
                })
            }

            this.attachmentFileNames = [...names]
            this.attachmentFiles = this.attachmentFileNames.map(name => ({
                name,
                url: null,
                isTemporary: false,
            }))
            this.$set(this.shipmentFormData, 'attachments', {
                files: this.attachmentFiles,
            })

            LEGACY_ATTACHMENT_KEYS.forEach(key => {
                delete this.shipmentFormData[key]
            })
        },
        async loadAttachments() {
            if (this.attachmentFileNames.length === 0) return

            const basePath = `${this.companyId}/purchases/${this.request.id}/AttachmentsCOMEX`

            for (const fileName of this.attachmentFileNames) {
                const paths = [basePath, `${basePath}/files`]
                let fileUrl = null

                for (const path of paths) {
                    try {
                        const storageRef = storage().ref(path)
                        const documentRef = storageRef.child(fileName)
                        fileUrl = await documentRef.getDownloadURL()
                        if (fileUrl) break
                    } catch (error) {
                        if (error.code !== 'storage/object-not-found') {
                            console.warn(
                                `Failed to load file ${fileName}:`,
                                error
                            )
                        }
                    }
                }

                const index = this.attachmentFiles.findIndex(
                    entry => entry.name === fileName && !entry.isTemporary
                )
                if (index > -1) {
                    this.$set(this.attachmentFiles[index], 'url', fileUrl)
                }
            }
        },
        resolveAttachmentName(file) {
            if (!file) return ''
            if (typeof file === 'string') return file
            if (file.name) return file.name
            if (file.filename) return file.filename
            if (file.file && file.file.name) return file.file.name
            return ''
        },
        attachmentFileKey(file) {
            const name = this.resolveAttachmentName(file)
            return name || 'Archivo'
        },
        getAttachmentDisplayName(file) {
            return this.attachmentFileKey(file)
        },
        triggerFileInput() {
            const fileInput = this.$refs.fileInput_files
            if (fileInput) {
                fileInput.click()
            }
        },
        handleFileSelect(event) {
            const files = event.target.files
            if (!files || files.length === 0) return

            const attachmentKey = ATTACHMENT_FILE_KEY

            if (!this.shipmentFormData.attachments) {
                this.$set(this.shipmentFormData, 'attachments', { files: [] })
            }
            if (!this.shipmentFormData.attachments[attachmentKey]) {
                this.$set(this.shipmentFormData.attachments, attachmentKey, [])
            }

            const newEntries = []
            for (let i = 0; i < files.length; i++) {
                const file = files[i]
                const displayName =
                    file.name ||
                    (file.webkitRelativePath &&
                        file.webkitRelativePath.split('/').pop()) ||
                    `archivo-${Date.now()}-${i}`
                const fileUrl = URL.createObjectURL(file)
                const entry = {
                    name: displayName,
                    url: fileUrl,
                    isTemporary: true,
                    type: file.type,
                    size: file.size,
                }
                newEntries.push(entry)

                this.localAttachments[attachmentKey][displayName] = {
                    file,
                    name: displayName,
                }
            }

            const updatedFiles = [...this.attachmentFiles, ...newEntries]
            this.$set(this, 'attachmentFiles', updatedFiles)
            this.$set(
                this.shipmentFormData.attachments,
                attachmentKey,
                updatedFiles
            )

            event.target.value = ''
            this.$nextTick(() => {
                this.structureData()
            })
        },
        openAttachment(file) {
            try {
                const fileName = this.getAttachmentDisplayName(file)
                if (file.isTemporary && file.url) {
                    window.open(file.url, '_blank')
                } else if (file.url) {
                    window.open(file.url, '_blank')
                } else {
                    this.$emit('openFile', fileName)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        removeAttachment(fileIndex) {
            const attachmentKey = ATTACHMENT_FILE_KEY

            if (!this.attachmentFiles[fileIndex]) {
                return
            }

            const file = this.attachmentFiles[fileIndex]
            const fileName = this.getAttachmentDisplayName(file)

            if (file.isTemporary) {
                if (
                    this.localAttachments[attachmentKey] &&
                    this.localAttachments[attachmentKey][fileName]
                ) {
                    delete this.localAttachments[attachmentKey][fileName]
                }
                if (file.url) {
                    URL.revokeObjectURL(file.url)
                }
            } else if (!this.shipmentIsNew) {
                this.$emit('deleteFile', fileName)
            }

            this.attachmentFiles = this.attachmentFiles.filter(
                (_, index) => index !== fileIndex
            )
            this.$set(
                this.shipmentFormData.attachments,
                attachmentKey,
                this.attachmentFiles
            )
            this.structureData()
        },
        buildAttachmentsForSave() {
            const names = this.attachmentFiles
                .map(file => this.resolveAttachmentName(file))
                .filter(Boolean)
            return names.length > 0 ? { files: names } : null
        },
        editShipment() {
            this.editable = !this.editable
        },
        deleteShipment() {
            this.$emit('deleteShipment', this.shipmentId)
        },
        structureData(code) {
            if (code) {
                const itemIndex = this.itemsFromShipment.findIndex(
                    obj => obj.code === code
                )
                if (itemIndex > -1) {
                    this.itemsFromShipment[itemIndex].totalPrice =
                        this.itemsFromShipment[itemIndex].unitPrice *
                        this.itemsFromShipment[itemIndex].quantity
                    this.getTotalValues()
                }
            }

            this.shipmentFormData.totalCOP = this.totalCOP
            this.shipmentFormData.totalUSD = this.totalUSD
            this.shipmentFormData.totalWeight = this.totalWeight
            this.shipmentFormData.totalQuantity = this.totalQuantity
            this.shipmentFormData.totalCustoms = this.computedTotalCustoms
            this.shipmentFormData.id = this.shipmentId

            const payload = { ...this.shipmentFormData }
            delete payload.editable
            LEGACY_ATTACHMENT_KEYS.forEach(key => {
                delete payload[key]
            })

            const attachmentsForSave = this.buildAttachmentsForSave()
            if (attachmentsForSave) {
                payload.attachments = attachmentsForSave
            } else {
                delete payload.attachments
            }

            this.$emit(
                'comexShipment',
                payload,
                this.itemsFromShipment,
                this.localAttachments
            )
        },
        pushItem(description) {
            let item = null
            this.itemsInRequest.forEach(obj => {
                if (obj.description === description.description) {
                    item = { ...obj }
                }
            })
            if (item) {
                const itemIndex = this.itemsFromShipment.findIndex(
                    obj => obj.description === description.description
                )

                if (itemIndex > -1) {
                    this.itemsFromShipment.splice(itemIndex, 1, item)
                    this.normalizeUsdUnitPrices()
                }
            }
        },
        addNewItem() {
            this.itemsAvailable = this.validateItems
            this.itemsFromShipment.push({})
        },
        deleteItem(item) {
            let itemIndex = null
            if (!item.code) {
                itemIndex = this.itemsFromShipment.findIndex(obj => !obj.code)
            } else {
                itemIndex = this.itemsFromShipment.findIndex(
                    obj => obj.code === item.code
                )
            }
            if (itemIndex > -1) {
                this.itemsFromShipment.splice(itemIndex, 1)
            }
            this.structureData()
        },
    },
}
</script>

<style>
.v-expansion-panel-header .v-btn .v-icon {
    transform: none !important;
}
</style>
